import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  Link,
  Grid,
} from '@mui/material';
import { CloudUpload as UploadIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import AuthService from '../services/AuthService';

/**
 * CSV Associator Component
 * Links customer data between two CSV files (JTL Ameise export and processed DATEV files)
 * Identifies Amazon customers by analyzing transaction data
 * Marks Amazon customers in the target file
 */
const CsvAssociator: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [files, setFiles] = useState<string[]>([]);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [tabIndex, setTabIndex] = useState(3); // For CSV Associator tab
  
  // References to file inputs for resetting
  const sourceFileInputRef = useRef<HTMLInputElement>(null);
  const targetFileInputRef = useRef<HTMLInputElement>(null);

  // Fetch file list on component mount and after operations
  useEffect(() => {
    fetchFiles();
  }, []);
  
  // Show success message effect
  useEffect(() => {
    if (parseSuccess) {
      // Save tab index in local storage before refresh
      localStorage.setItem('activeTabIndex', tabIndex.toString());
      localStorage.setItem('showParseSuccess', 'true');
      
      // Refresh the page after a short delay
      const timer = setTimeout(() => {
        window.location.reload();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [parseSuccess, tabIndex]);

  // Check for success message after page reload
  useEffect(() => {
    const savedTabIndex = localStorage.getItem('activeTabIndex');
    const showSuccess = localStorage.getItem('showParseSuccess');
    
    if (showSuccess === 'true') {
      setSuccess('Erfolgreich verarbeitet und gespeichert');
      
      // Clear the flag
      localStorage.removeItem('showParseSuccess');
      
      // Auto-hide the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const fetchFiles = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/assoc/files`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Failed to fetch files (Status: ${response.status})`);
      }

      const data = await response.json();
      setFiles(data.files || []);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError((err as Error).message);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, fileType: 'source' | 'target') => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append(fileType, files[0]);

    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus(`${fileType === 'source' ? 'Quell' : 'Ziel'}-Datei wird hochgeladen...`);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/assoc/upload`, {
        method: "POST",
        body: formData,
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      resetFileInput(fileType);

      if (!response.ok) {
        throw new Error(`Fehler beim Hochladen der Datei (Status: ${response.status})`);
      }

      const result = await response.json();
      setSuccess(`${fileType === 'source' ? 'Quell' : 'Ziel'}-Datei erfolgreich hochgeladen`);
      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error in file upload:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  const processFiles = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setProcessingStatus("Dateien werden verarbeitet...");

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/assoc/process`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        throw new Error(`Fehler beim Verarbeiten der Dateien (Status: ${response.status})`);
      }

      const result = await response.json();
      console.log('Process result:', result);
      
      const identifiedCount = result.matchedCustomers || 0;
      const matchedCount = result.matchesInTargetFile || 0;
      
      // Check if we have any diagnostic messages
      if (result.diagnostics) {
        console.warn('Server diagnostic message:', result.diagnostics);
        setSuccess(`Dateien verarbeitet, aber mit Warnungen: ${result.diagnostics}`);
        
        // Also set an error to be more visible if no Amazon customers found
        if (identifiedCount === 0) {
          setError('Keine Amazon-Kunden identifiziert. Prüfen Sie das Dateiformat und den Inhalt. Es sollte eine Spalte mit "Amazon" Text enthalten und Kundennummern sollten mit "KD" beginnen.');
        }
      } else {
        setSuccess(`Dateien erfolgreich verarbeitet. ${identifiedCount} Amazon-Kunden identifiziert, ${matchedCount} Kunden in der Zieldatei markiert.`);
      }
      
      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error processing files:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setProcessingStatus("");
    }
  };

  // Function to delete a file from the server
  const deleteFile = async (filename: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/assoc/delete/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload();
          return;
        }
        throw new Error(`Fehler beim Löschen der Datei (Status: ${response.status})`);
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      // Don't show error to user, just log it
    }
  };

  const downloadFile = async (filename: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/jtl-helper/assoc/download/${encodeURIComponent(filename)}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
      });

      if (!response.ok) {
        // Check if token expired (403 error)
        if (response.status === 403) {
          setError("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
          AuthService.logout();
          window.location.reload(); // Reload to show login page
          return;
        }
        throw new Error(`Fehler beim Herunterladen der Datei (Status: ${response.status})`);
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Delete the source and target files
      const sourceFile = files.find(f => f.startsWith('src_'));
      const targetFile = files.find(f => f.startsWith('trg_'));
      
      // Delete all files in the directory
      if (sourceFile) await deleteFile(sourceFile);
      if (targetFile) await deleteFile(targetFile);
      await deleteFile(filename);
      
      // Set success flag to trigger page refresh
      setParseSuccess(true);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError((err as Error).message);
    }
  };

  // Function to reset the file input
  const resetFileInput = (fileType: 'source' | 'target') => {
    if (fileType === 'source' && sourceFileInputRef.current) {
      sourceFileInputRef.current.value = '';
    } else if (fileType === 'target' && targetFileInputRef.current) {
      targetFileInputRef.current.value = '';
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        CSV Zuordner
      </Typography>
      
      <Typography variant="body1" paragraph>
        Als Quell-Datei muss die von JTL Ameise (für die Buchhaltung) exportierte CSV Datei für den jeweiligen
        Monat hochgeladen werden. Wichtig dabei sind die Spalten: KD Nummer, RG Nummer und Anmerkung (Vorlage verwenden).
        Hierbei geht es darum, zu erkennen, welche Kunden von Amazon kommen.
      </Typography>
      
      <Typography variant="body2" paragraph>
        Hochgeladen werden muss:
        <ul>
          <li>Quelle: JTL Ameise Export (Rechnungen (neu)) von Vorlage</li>
          <li>Ziel: Verkauf (bereits geparst!): Nur "EXTF_Stamm_DebKred_xxxx_parsed.csv"</li>
        </ul>
      </Typography>
      
      {processingStatus && (
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>{processingStatus}</Typography>
        </Box>
      )}

      <Box mb={4} display="flex" flexDirection="column" gap={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>1. Quell-Datei hochladen:</Typography>
            <input 
              ref={sourceFileInputRef}
              accept=".csv" 
              style={{ display: "none" }} 
              id="source-file-upload" 
              type="file" 
              onChange={(e) => handleFileUpload(e, 'source')} 
              disabled={loading} 
            />
            <label htmlFor="source-file-upload">
              <Button 
                variant="outlined" 
                component="span" 
                disabled={loading}
                fullWidth
              >
                Quell-CSV hochladen
              </Button>
            </label>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>2. Ziel-Datei hochladen:</Typography>
            <input 
              ref={targetFileInputRef}
              accept=".csv" 
              style={{ display: "none" }} 
              id="target-file-upload" 
              type="file" 
              onChange={(e) => handleFileUpload(e, 'target')} 
              disabled={loading} 
            />
            <label htmlFor="target-file-upload">
              <Button 
                variant="outlined" 
                component="span" 
                disabled={loading}
                fullWidth
              >
                Ziel-CSV hochladen
              </Button>
            </label>
          </Grid>
          
          <Grid item xs={12}>
            <Box mt={2}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={processFiles}
                disabled={loading || files.filter(f => f.startsWith('src_') || f.startsWith('trg_')).length < 2}
                fullWidth
              >
                3. Verarbeiten
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      
      {files.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>
            4. Dateien:
          </Typography>
          
          <List>
            {files.map((file, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                {file.includes('_assoc') ? (
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={() => downloadFile(file)}
                  >
                    {file} (Herunterladen)
                  </Link>
                ) : (
                  <Typography variant="body2">{file}</Typography>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">
          <Typography>{success}</Typography>
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CsvAssociator;